// frontend/src/utils/gaEvents.ts
import ReactGA from 'react-ga4';
import {
  resolveAdsConversionSendTo,
  resolveCommerceValue,
  resolveGaMeasurementId,
  resolveGoogleAdsId,
} from '../config/googleAds.js';

/** Module consent gate — RouteTracker must use this, not ReactGA.isInitialized alone. */
let analyticsConsentActive = false;
let analyticsConsentRevision = 0;
export const getAnalyticsConsentRevision = (): number => analyticsConsentRevision;
let productContext: Record<string, string> = {};
/** First /me create can finish before cookie consent restore — flush after initializeGA. */
let pendingSignUp: { method?: string; plan?: string } | null = null;
const DEBUG_GA_SESSION_KEY = 'aiq_debug_ga';
const consentListeners = new Set<() => void>();
export function subscribeAnalyticsConsent(listener: () => void): () => void {
  consentListeners.add(listener);
  return () => { consentListeners.delete(listener); };
}
export function setAnalyticsProductContext(context: { plan: string; auth_state: string }): void {
  productContext = context;
}
function notifyConsent(): void {
  consentListeners.forEach(listener => { try { listener(); } catch { /* Best effort. */ } });
}

type GtagFn = (...args: unknown[]) => void;
type AiqWindow = Window & {
  gtag?: GtagFn;
  dataLayer?: unknown[];
  /** Injected by index.html — starts deferred gtag.js (idempotent). */
  __aiqLoadGtag?: () => void;
};

function getWindowGtag(): GtagFn | null {
  if (typeof window === 'undefined') return null;
  const w = window as AiqWindow;
  if (typeof w.gtag === 'function') return w.gtag;
  return null;
}

/**
 * Ensure gtag.js is loading. index.html defers the script past LCP (idle);
 * call this on cookie Accept so consent grant is not stuck behind the idle timer.
 */
export function ensureGtagLoaded(): void {
  if (typeof window === 'undefined') return;
  const load = (window as AiqWindow).__aiqLoadGtag;
  if (typeof load === 'function') load();
}

/**
 * Always prefer window.gtag (index.html / gtag.js).
 * ReactGA.gtag can race with a second loader overwrite during initialize.
 */
function pushGtag(...args: unknown[]): void {
  const gtag = getWindowGtag();
  if (gtag) {
    gtag(...args);
    return;
  }
  if (ReactGA.isInitialized) {
    ReactGA.gtag(...args);
  }
}

/**
 * DebugView needs debug_mode on events. Query ?aiq_debug_ga=1 is lost after Entra redirect
 * and most SPA navigations — persist in sessionStorage for the tab.
 */
function wantsGaDebug(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const flag = new URLSearchParams(window.location.search).get('aiq_debug_ga');
    if (flag === '1') {
      try { sessionStorage.setItem(DEBUG_GA_SESSION_KEY, '1'); } catch { /* quota */ }
      return true;
    }
    if (flag === '0') {
      try { sessionStorage.removeItem(DEBUG_GA_SESSION_KEY); } catch { /* ignore */ }
      return false;
    }
    return sessionStorage.getItem(DEBUG_GA_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

function flushPendingSignUp(): void {
  if (!pendingSignUp || !analyticsConsentActive) return;
  const pending = pendingSignUp;
  pendingSignUp = null;
  trackEvent(GA_FUNNEL.SIGN_UP, {
    method: pending.method || 'entra',
    plan: pending.plan || 'Free',
  });
}

const CONSENT_DENIED = {
  ad_storage: 'denied',
  ad_user_data: 'denied',
  ad_personalization: 'denied',
  analytics_storage: 'denied',
} as const;

const CONSENT_GRANTED = {
  ad_storage: 'granted',
  ad_user_data: 'granted',
  ad_personalization: 'granted',
  analytics_storage: 'granted',
} as const;

/** Consent Mode v2 update — call on accept / decline / restore (before or after gtag load). */
export const updateGoogleConsent = (accepted: boolean): void => {
  pushGtag('consent', 'update', accepted ? CONSENT_GRANTED : CONSENT_DENIED);
};

/** Funnel + product event names (GA4). Keep stable for reporting. */
export const GA_FUNNEL = {
  TRAFFIC_LANDING: 'traffic_landing',
  CTA_START: 'cta_start_clicked',
  INTENT_SELECTED: 'intent_selected',
  CLASSIC_TEMPLATE: 'classic_template_selected',
  ASSESSMENT_STARTED: 'assessment_started',
  ANSWER_SELECTED: 'answer_selected',
  AGENTIC_ANSWER: 'agentic_answer_selected',
  ARCHITECTURE_PREVIEW: 'architecture_preview_shown',
  /** Preview family near-miss strip — Use instead (does not auto-switch). */
  ARCHITECTURE_NEAR_MISS_USE_INSTEAD: 'architecture_near_miss_use_instead',
  /** Results (or preview-related) related strip — Use instead. */
  ARCHITECTURE_RELATED_USE_INSTEAD: 'architecture_related_use_instead',
  /** Results (or preview-related) related strip — Add to diagram / compose. */
  ARCHITECTURE_RELATED_ADD: 'architecture_related_add',
  PACK_READY: 'agentic_pack_ready',
  AGENTIC_EXPORT: 'agentic_export',
  PACK_REFRESH: 'pack_refresh',
  FORK_AFTER_EXPORT: 'fork_after_export',
  ENGAGEMENT_SAVED: 'engagement_saved',
  WORKSHOP_SHARE: 'workshop_share_created',
  PREMIUM_LOCK: 'premium_lock_clicked',
  /** Paywall button attribution (cta = sign_in | pack_pass | pro | enterprise | feedback). */
  PAYWALL_CTA: 'paywall_cta_clicked',
  /** Guest chose create/sign-in before MSAL (acquisition intent). */
  SIGN_UP_INTENT: 'sign_up_intent',
  CHECKOUT_STARTED: 'checkout_started',
  PACK_PASS_CHECKOUT: 'pack_pass_checkout_started',
  PACK_PASS_PURCHASE: 'pack_pass_purchase_success',
  PURCHASE_SUCCESS: 'purchase_success',
  /** First Cosmos profile create — primary Ads/GA4 acquisition conversion. */
  SIGN_UP: 'sign_up',
  PROJECT_SAVE_SUCCESS: 'project_save_success',
  PROJECT_SAVE_ERROR: 'project_save_error',
  AGENTIC_STEP_VIEW: 'agentic_step_view',
  AGENTIC_STEP_COMPLETED: 'agentic_step_completed',
} as const;

export type GaFunnelEvent = (typeof GA_FUNNEL)[keyof typeof GA_FUNNEL];

/** True only while user consent is granted and GA was initialized this session. */
export const isAnalyticsActive = (): boolean => analyticsConsentActive;

export const initializeGA = () => {
  const measurementId = resolveGaMeasurementId();
  const adsId = resolveGoogleAdsId();
  if (!measurementId) return;

  if (!analyticsConsentActive) {
    // Do not wait for idle — Accept must start gtag immediately (CWV-safe defer in index.html).
    ensureGtagLoaded();

    // Grant Consent Mode first so the next config/event is not dropped as denied.
    updateGoogleConsent(true);

    const debug = wantsGaDebug();
    const gtagOpts: Record<string, unknown> = {
      send_page_view: false,
      ...(debug ? { debug_mode: true } : {}),
    };

    // Re-config after grant (dataLayer queues until deferred gtag.js lands; ReactGA helps events).
    pushGtag('config', measurementId, gtagOpts);
    if (adsId) pushGtag('config', adsId);

    const configs: Array<{ trackingId: string; gtagOptions?: Record<string, unknown> }> = [
      { trackingId: measurementId, gtagOptions: gtagOpts },
    ];
    if (adsId) configs.push({ trackingId: adsId });

    ReactGA.initialize(configs, {
      // Always send in production builds; local CRA uses testMode to avoid noise.
      testMode: process.env.NODE_ENV === 'development',
    });

    // Consent again after ReactGA may rewrite window.gtag during its script inject.
    updateGoogleConsent(true);
    pushGtag('config', measurementId, gtagOpts);

    analyticsConsentActive = true;
    analyticsConsentRevision += 1;
    notifyConsent();

    // Immediate diagnostic hit so Realtime is not empty while waiting for navigation.
    const path = typeof window !== 'undefined'
      ? `${window.location.pathname}${window.location.search}`
      : '/';
    pushGtag('event', 'page_view', {
      page_path: path,
      page_title: typeof document !== 'undefined' ? document.title : 'Architect-IQ',
      ...(debug ? { debug_mode: true } : {}),
    });
    pushGtag('event', 'aiq_consent_granted', {
      measurement_id: measurementId,
      ...(debug ? { debug_mode: true } : {}),
    });

    // /me may have already reported accountCreated while consent was still restoring.
    flushPendingSignUp();

    if (debug) {
      console.log('GA initialized after consent', {
        measurementId,
        adsId: adsId || null,
        debug,
      });
    }
  }
};

/**
 * Soft-disable after decline / cookie reset.
 * Custom events and RouteTracker pageviews stop; gtag script may remain loaded.
 */
export const disableGA = () => {
  analyticsConsentActive = false;
  analyticsConsentRevision += 1;
  notifyConsent();
  updateGoogleConsent(false);
};

function fireOptionalAdsConversion(
  kind: 'sign_up' | 'purchase' | 'pack_pass_purchase' | 'begin_checkout',
  params: Record<string, unknown> = {}
): void {
  const sendTo = resolveAdsConversionSendTo(kind);
  if (!sendTo || !analyticsConsentActive) return;
  pushGtag('event', 'conversion', { send_to: sendTo, ...params });
}

function fireCommerceEvent(
  eventName: 'begin_checkout' | 'purchase',
  sku: 'Pro' | 'pack_pass',
  currency?: string,
  extra: Record<string, unknown> = {}
): void {
  if (!analyticsConsentActive) return;
  const commerce = resolveCommerceValue(sku, currency);
  if (!commerce) return;

  const payload: Record<string, unknown> = {
    currency: commerce.currency,
    value: commerce.value,
    items: [
      {
        item_id: commerce.item_id,
        item_name: commerce.item_name,
        quantity: 1,
        price: commerce.value,
      },
    ],
    ...extra,
  };

  pushGtag('event', eventName, payload);

  if (eventName === 'begin_checkout') {
    fireOptionalAdsConversion('begin_checkout', {
      value: commerce.value,
      currency: commerce.currency,
    });
  } else if (sku === 'Pro') {
    fireOptionalAdsConversion('purchase', {
      value: commerce.value,
      currency: commerce.currency,
      transaction_id: extra.transaction_id,
    });
  } else {
    fireOptionalAdsConversion('pack_pass_purchase', {
      value: commerce.value,
      currency: commerce.currency,
      transaction_id: extra.transaction_id,
    });
  }
}

export const trackEvent = (action: string, params: Record<string, unknown> = {}) => {
  if (!analyticsConsentActive) {
    return;
  }

  if (!action) {
    console.error('GA Track Event: Attempt to send event without a name (action).', params);
    return;
  }

  const payload: Record<string, unknown> = {
    ...productContext,
    ...params,
    path: params.path || (typeof window !== 'undefined' ? window.location.pathname : undefined),
    page_title: params.page_title || (typeof document !== 'undefined' ? document.title : undefined),
  };
  // DebugView only lists hits with debug_mode; keep it on every event while the tab is debugging.
  if (wantsGaDebug()) {
    payload.debug_mode = true;
  }

  // Prefer window.gtag (same path as consent page_view). ReactGA.event alone often misses DebugView.
  if (getWindowGtag() || ReactGA.isInitialized) {
    pushGtag('event', action, payload);
  } else {
    ReactGA.event(action, payload);
  }

  // Dual-write GA4 ecommerce + optional Ads conversion labels for paid funnel.
  if (action === GA_FUNNEL.SIGN_UP) {
    // GA4 recommended event already sent above; also fire Ads conversion label when configured.
    fireOptionalAdsConversion('sign_up', {
      method: payload['method'] || 'entra',
    });
  } else if (action === GA_FUNNEL.CHECKOUT_STARTED) {
    fireCommerceEvent('begin_checkout', 'Pro', payload['currency'] as string | undefined);
  } else if (action === GA_FUNNEL.PACK_PASS_CHECKOUT) {
    fireCommerceEvent('begin_checkout', 'pack_pass', payload['currency'] as string | undefined);
  } else if (action === GA_FUNNEL.PURCHASE_SUCCESS) {
    // Revenue commerce requires verified Stripe session id (not bare ?payment=success).
    if (payload['transaction_id']) {
      fireCommerceEvent('purchase', 'Pro', payload['currency'] as string | undefined, {
        transaction_id: payload['transaction_id'],
      });
    }
  } else if (action === GA_FUNNEL.PACK_PASS_PURCHASE) {
    if (payload['transaction_id']) {
      fireCommerceEvent('purchase', 'pack_pass', payload['currency'] as string | undefined, {
        transaction_id: payload['transaction_id'],
      });
    }
  }
};

/** Fire once when `/api/me` reports a brand-new Cosmos profile (Ads primary conversion). */
export const trackSignUp = (params: { method?: string; plan?: string } = {}) => {
  if (!analyticsConsentActive) {
    pendingSignUp = {
      method: params.method || 'entra',
      plan: params.plan || 'Free',
    };
    return;
  }
  trackEvent(GA_FUNNEL.SIGN_UP, {
    method: params.method || 'entra',
    plan: params.plan || 'Free',
  });
};

/** Board-pack ready (AI or fallback) — mid-funnel after intent_selected. */
export const trackPackReady = (params: {
  intent?: string | null;
  mode?: string | null;
  pack_source: 'ai' | 'fallback';
  pattern_id?: string | null;
  degraded?: boolean;
}) => {
  trackEvent(GA_FUNNEL.PACK_READY, {
    intent: params.intent || 'full',
    mode: params.mode || 'full',
    pack_source: params.pack_source,
    pattern_id: params.pattern_id || undefined,
    degraded: Boolean(params.degraded),
  });
};

export const trackAgenticExport = (format: 'pdf' | 'json' | 'markdown' | 'notion') => {
  trackEvent(GA_FUNNEL.AGENTIC_EXPORT, { format });
};

/** Regenerated a stale / missing board pack (Wave M1 retention). */
export const trackPackRefresh = (params: {
  intent?: string | null;
  stale?: boolean;
}) => {
  trackEvent(GA_FUNNEL.PACK_REFRESH, {
    intent: params.intent || 'full',
    stale: Boolean(params.stale),
  });
};

/** Fork from the post-export banner (or equivalent keep-refreshing CTA). */
export const trackForkAfterExport = (params: {
  intent?: string | null;
  source?: string;
}) => {
  trackEvent(GA_FUNNEL.FORK_AFTER_EXPORT, {
    intent: params.intent || 'full',
    source: params.source || 'post_export_banner',
  });
};

export const trackPremiumLockClick = (surface?: string) => {
  trackEvent(GA_FUNNEL.PREMIUM_LOCK, {
    surface: surface || 'premium_lock',
    path: typeof window !== 'undefined' ? window.location.pathname : undefined,
  });
};

export type ArchitecturePatternActionSurface =
  | 'near_miss_preview'
  | 'related_preview'
  | 'related_results';

/**
 * Architecture switch / compose funnel — compare near-miss Use instead (preview)
 * vs related Use instead / Add (Results) for Add discoverability.
 * Never send answer text or PII — pattern ids only.
 */
export const trackArchitecturePatternAction = (params: {
  action: 'use_instead' | 'add';
  surface: ArchitecturePatternActionSurface;
  fromPatternId?: string | null;
  toPatternId: string;
  intent?: string | null;
}) => {
  const toId = String(params.toPatternId || '');
  if (!toId.startsWith('PATTERN_')) return;

  let eventName: GaFunnelEvent = GA_FUNNEL.ARCHITECTURE_RELATED_USE_INSTEAD;
  if (params.action === 'add') {
    eventName = GA_FUNNEL.ARCHITECTURE_RELATED_ADD;
  } else if (params.surface === 'near_miss_preview') {
    eventName = GA_FUNNEL.ARCHITECTURE_NEAR_MISS_USE_INSTEAD;
  }

  const fromId = params.fromPatternId && String(params.fromPatternId).startsWith('PATTERN_')
    ? String(params.fromPatternId)
    : undefined;

  trackEvent(eventName, {
    action: params.action,
    surface: params.surface,
    from_pattern_id: fromId,
    to_pattern_id: toId,
    intent: params.intent || undefined,
  });
};

export const trackPackPassCheckout = (params: {
  engagementId?: string | null;
  source?: string;
  currency?: string;
}) => {
  trackEvent(GA_FUNNEL.PACK_PASS_CHECKOUT, {
    sku: 'pack_pass',
    engagement_id: params.engagementId || undefined,
    source: params.source || 'paywall',
    currency: params.currency,
  });
};

export const trackPackPassPurchase = (params: {
  source?: string;
  currency?: string;
  transactionId?: string;
} = {}) => {
  trackEvent(GA_FUNNEL.PACK_PASS_PURCHASE, {
    sku: 'pack_pass',
    source: params.source || 'agentic_return',
    currency: params.currency,
    transaction_id: params.transactionId,
  });
};
